import { useState, useEffect, useRef } from "react"
import { router } from "@inertiajs/react"
import { Textarea } from "@/components/ui/textarea"
import MediaUploader from "./media_uploader"
import LinkedinProfileInfo from "./linkedin_profile_info"
import LinkedinFirstComment from "./linkedin_first_comment"
import VoiceNoteWidget from "@/components/audio/voice_note_widget"

const LinkedinThreadEditor = ({
  posts,
  onUpdatePost,
  onAddPost,
  onRemovePost,
  joinedContent,
  initialContent,
  initialReplyContent = "",
  replyId: initialReplyId = null,
  saveUrl,
  onSaveStart,
  onSaveEnd,
  assets = [],
  onMediaChange,
  mediaSelection,
  displayName,
  handleLabel,
  avatarUrl,
  disabled,
  organizationId,
  onReplyChange,
}) => {
  const [saveTimeout, setSaveTimeout] = useState(null)
  const [replyContent, setReplyContent] = useState(initialReplyContent)
  const [replyId, setReplyId] = useState(initialReplyId)
  const [firstCommentOpen, setFirstCommentOpen] = useState(!!initialReplyContent || !!initialReplyId)
  const postsRef = useRef(posts)

  useEffect(() => {
    postsRef.current = posts
  }, [posts])

  useEffect(() => {
    // Keep local state in sync with Inertia-provided props
    setReplyContent(initialReplyContent)
    setReplyId(initialReplyId)
    setFirstCommentOpen(!!initialReplyContent || !!initialReplyId)
  }, [initialReplyContent, initialReplyId])

  useEffect(() => {
    if (!saveUrl || !onSaveStart || !onSaveEnd || typeof joinedContent === 'undefined' || typeof initialContent === 'undefined') {
      if (saveTimeout) clearTimeout(saveTimeout);
      return;
    }

    if (saveTimeout) clearTimeout(saveTimeout)

    if (joinedContent === initialContent && replyContent === initialReplyContent) {
      return
    }

    const timeout = setTimeout(() => {
      onSaveStart()

      const data = { post: { linkedin_content: joinedContent } }

      // Include reply attributes when reply content exists
      if (replyContent.trim()) {
        data.post.reply_attributes = {
          content: replyContent.trim(),
          organization_id: organizationId
        }
        if (replyId) {
          data.post.reply_attributes.id = replyId
        }
      } else if (replyId) {
        // Remove reply if content is empty but replyId exists
        data.post.reply_attributes = {
          id: replyId,
          _destroy: true
        }
      }

      const options = {
        preserveScroll: true,
        preserveState: true,
        onSuccess: (page) => {
          // Update replyId from response if created
          const newReplyId = page?.props?.post?.reply?.id
          if (newReplyId) {
            setReplyId(newReplyId)
          }
        },
        onError: () => { /* Parent handles state */ },
        onFinish: () => onSaveEnd()
      }

      router.patch(saveUrl, data, options)

    }, 750)

    setSaveTimeout(timeout)

    return () => {
      if (timeout) clearTimeout(timeout)
    }
  }, [joinedContent, initialContent, replyContent, initialReplyContent, saveUrl, onSaveStart, onSaveEnd, replyId])

  const handleSaveReply = (content) => {
    setReplyContent(content)
    onReplyChange?.(content)
  }

  const handleDeleteReply = (_id) => {
    setReplyContent("")
    setReplyId(null)
    onReplyChange?.("")
  }

  const appendTranscript = (transcript) => {
    if (!transcript) return

    const currentContent = postsRef.current.join('\n\n')
    const separator = currentContent.trim() ? "\n\n" : ""
    onUpdatePost(0, `${currentContent}${separator}${transcript}`)
  }

  return (
    <div className="space-y-4 rounded-lg p-4">
      <LinkedinProfileInfo
        displayName={displayName}
        handleLabel={handleLabel}
        avatarUrl={avatarUrl}
      />

      <Textarea
        value={posts.join('\n\n')}
        onChange={(e) => {
          onUpdatePost(0, e.target.value)
        }}
        placeholder={`Share your thoughts...`}
        disabled={disabled}
        className="min-h-[100px] !border-0 !bg-transparent !ring-0 !ring-offset-0 focus-visible:!ring-0 resize-none overflow-hidden"
      />

      {mediaSelection?.url && (
        <div className="flex justify-center">
          <MediaUploader
            assets={assets}
            onChange={onMediaChange}
            currentSelection={mediaSelection}
            disabled={disabled}
          />
        </div>
      )}

      {!disabled && (
        <div className="flex min-h-9 items-center gap-1">
          <div className="flex h-7 items-center gap-1">
            {!mediaSelection?.url && (
              <MediaUploader
                assets={assets}
                onChange={onMediaChange}
                currentSelection={mediaSelection}
                disabled={disabled}
                compactTrigger
              />
            )}
            <LinkedinFirstComment
              initialReplyContent={initialReplyContent}
              replyId={replyId}
              onSave={handleSaveReply}
              onDelete={handleDeleteReply}
              displayName={displayName}
              handleLabel={handleLabel}
              avatarUrl={avatarUrl}
              disabled={disabled}
              open={firstCommentOpen}
              onOpenChange={setFirstCommentOpen}
              triggerOnly
            />
          </div>
          <VoiceNoteWidget
            variant="toolbar"
            createHighlight={false}
            onTranscript={appendTranscript}
          />
        </div>
      )}

      <LinkedinFirstComment
        initialReplyContent={initialReplyContent}
        replyId={replyId}
        onSave={handleSaveReply}
        onDelete={handleDeleteReply}
        displayName={displayName}
        handleLabel={handleLabel}
        avatarUrl={avatarUrl}
        disabled={disabled}
        open={firstCommentOpen}
        onOpenChange={setFirstCommentOpen}
        editorOnly
      />

    </div>
  )
}

export default LinkedinThreadEditor
